import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { PageContainer, Breadcrumb } from 'styles/common.styles';
import { SEARCH_LABEL_W } from 'styles/theme';
import { ActionButtonGroup, FormArea, getCurrentFiscalYear } from 'components/forms';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import { showAlert, showConfirm } from 'utils/toastBridge';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { useAuth } from 'contexts/AuthContext';
import { loadDepts, filterDepts } from 'master/deptStore';
import { loadOrSeedProjects } from 'budget/budgetProjectStore';
import {
  ensureFormCatalog,
  resolveActiveFormForTenant,
} from 'budget/bizFormCatalog';
import {
  mergeFieldMaps,
  normalizeFieldValue,
  lookupKindForInput,
} from 'budget/bizPlanHwpFieldApply';
import { createBizPlanEditor } from 'budget/rhwp/rhwpEditorHost';
import {
  listBizPlans,
  getBizPlan,
  upsertBizPlan,
  removeBizPlan,
  createPlanId,
} from 'budget/bizPlanStore';
import { loadBizPlanMock } from 'budget/bizPlanImportToExpense';

const LIST_GRID_ID = 'biz-plan-list';

/** 편성요구 이관용 — HWP 직접입력만으로는 추출이 불안정해 화면 입력도 병행 */
const FORMATION_DRAFT_DEFAULTS = {
  subjectName: '',
  planAmt: '',
  calcDetail: '',
  costTotal: '',
  narrative: '',
};

function draftToFieldMap(draft = {}, picked = {}) {
  const map = {};
  if (picked.deptName) map['header.deptName'] = picked.deptName;
  if (picked.projectName) map['doc.title'] = picked.projectName;
  if (normalizeFieldValue(draft.subjectName)) {
    map['calc.line1.subjectName'] = String(draft.subjectName).trim();
  }
  if (normalizeFieldValue(draft.planAmt)) {
    map['calc.line1.planAmt'] = String(draft.planAmt).trim();
  }
  if (normalizeFieldValue(draft.calcDetail)) {
    map['calc.line1.calcDetail'] = String(draft.calcDetail).trim();
  }
  if (normalizeFieldValue(draft.costTotal)) {
    map['overview.costTotal'] = String(draft.costTotal).trim();
  }
  if (normalizeFieldValue(draft.narrative)) {
    map['calcBasis.narrative'] = String(draft.narrative).trim();
  }
  return map;
}

function fieldsToDraft(fields = {}) {
  return {
    subjectName: normalizeFieldValue(fields['calc.line1.subjectName']) || '',
    planAmt: normalizeFieldValue(fields['calc.line1.planAmt']) || '',
    calcDetail: normalizeFieldValue(fields['calc.line1.calcDetail']) || '',
    costTotal: normalizeFieldValue(fields['overview.costTotal']) || '',
    narrative: normalizeFieldValue(fields['calcBasis.narrative']) || '',
  };
}

const Page = styled(PageContainer)`
  padding: 4px 6px 6px;
  gap: 4px;
`;

const TopBar = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const CompactTitle = styled.h1`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--erp-text, #111827);
  white-space: nowrap;
`;

const FileMeta = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 11px;
  color: var(--erp-text-secondary, #6b7280);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 11px;
  color: var(--erp-text-secondary, #6b7280);
  line-height: 1.35;
`;

const Shell = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ListHost = styled.div`
  flex: 0 0 132px;
  min-height: 110px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const EditorHost = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  border: 1px solid var(--erp-border, #e5e7eb);
  border-radius: 4px;
  overflow: hidden;
  background: #fff;

  & > iframe {
    position: absolute !important;
    inset: 0 !important;
    width: 100% !important;
    height: 100% !important;
    border: none;
  }
`;

const LogPanel = styled.pre`
  margin: 0;
  flex: 0 0 auto;
  max-height: ${(p) => (p.$open ? '120px' : '0')};
  overflow: ${(p) => (p.$open ? 'auto' : 'hidden')};
  padding: ${(p) => (p.$open ? '6px 8px' : '0')};
  font-size: 11px;
  line-height: 1.35;
  background: var(--erp-surface-muted, #f9fafb);
  border: ${(p) => (p.$open ? '1px solid var(--erp-border, #e5e7eb)' : 'none')};
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-all;
  transition: max-height 0.15s ease;
`;

const SyncPanel = styled.div`
  flex: 0 0 auto;
  padding: 6px 8px;
  border: 1px solid var(--erp-border, #e5e7eb);
  border-radius: 4px;
  background: var(--erp-surface-muted, #f9fafb);
`;

/**
 * 사업계획서 rhwp 작성
 * — 목록 IndexedDB CRUD · 누름틀 Field API(또는 core 우회) · 부서/사업 팝업
 */
const BizPlanRhwpPoc = () => {
  const tenantCode = useTenantCode();
  const { user } = useAuth();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const hostRef = useRef(null);
  const editorRef = useRef(null);
  const listGridRef = useRef(null);
  const fileInputRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [panelJson, setPanelJson] = useState('// 저장·필드 로그 — 「로그」로 펼침');
  const [fileName, setFileName] = useState('');
  const [logOpen, setLogOpen] = useState(false);
  const [chromeCompact, setChromeCompact] = useState(true);
  const [activeForm, setActiveForm] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [picked, setPicked] = useState({
    deptCode: '',
    deptName: '',
    projectCode: '',
    projectName: '',
  });
  const [lookup, setLookup] = useState({ open: false, type: null });

  const draftMethods = useForm({
    defaultValues: FORMATION_DRAFT_DEFAULTS,
  });
  const { getValues: getDraftValues, reset: resetDraft } = draftMethods;

  const fiscalYear = getCurrentFiscalYear();

  const resolveBizForm = useCallback(
    (overridePicked) => {
      const p = overridePicked || picked;
      return resolveActiveFormForTenant(tenantCode, {
        deptCode: p.deptCode || '',
        projectCode: p.projectCode || '',
        fiscalYear,
        userId: user?.id || '',
      });
    },
    [tenantCode, picked, fiscalYear, user],
  );

  const listGrid = useHiGridHook({
    gridRef: listGridRef,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
    defaultRow: {},
  });

  const listColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '계획ID', field: 'planId', size: 150 }),
      hiCol.roTextColumn({ header: '사업명', field: 'title', size: 200 }),
      hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 120 }),
      hiCol.roTextColumn({ header: '상태', field: 'status', size: 70 }),
      hiCol.roTextColumn({ header: '저장시각', field: 'updatedAt', size: 150 }),
    ],
    [],
  );

  const syncDraftFields = useMemo(
    () => [
      [
        {
          name: 'subjectName',
          label: '편성목',
          type: 'text',
          width: 160,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'planAmt',
          label: '예산액',
          type: 'text',
          width: 100,
          labelWidth: SEARCH_LABEL_W,
          placeholder: '천원',
        },
        {
          name: 'costTotal',
          label: '총사업비',
          type: 'text',
          width: 100,
          labelWidth: SEARCH_LABEL_W,
          placeholder: '천원',
        },
      ],
      [
        {
          name: 'calcDetail',
          label: '산출내역',
          type: 'text',
          width: 280,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'narrative',
          label: '산출근거',
          type: 'text',
          width: 280,
          labelWidth: SEARCH_LABEL_W,
        },
      ],
    ],
    [],
  );

  const refreshList = useCallback(async () => {
    const rows = await listBizPlans({ tenantCode, fiscalYear });
    const mapped = rows.map((r) => ({
      ...r,
      id: r.planId,
      deptName: r.picked?.deptName || r.fields?.['header.deptName'] || '',
    }));
    listGridRef.current?.setData?.(mapped);
    return mapped;
  }, [tenantCode, fiscalYear]);

  const loadBuffer = useCallback(
    async (buffer, name, meta = {}) => {
      const ed = editorRef.current;
      if (!ed) return;
      const result = await ed.loadFile(buffer, name, { suppressDialogs: true });
      setFileName(name);
      setPanelJson(
        JSON.stringify(
          {
            gate: 'load',
            fileName: name,
            formId: meta.formId || activeForm?.formId || null,
            planId: meta.planId || planId,
            pageCount: result?.pageCount,
            loadedAt: new Date().toISOString(),
          },
          null,
          2,
        ),
      );
    },
    [activeForm, planId],
  );

  const loadTenantForm = useCallback(async () => {
    ensureFormCatalog();
    const form = resolveBizForm();
    setActiveForm(form);
    if (!form?.publicUrl && !form?.hasUploadedTemplate) {
      showAlert('기관에 연결된 양식이 없습니다. 양식관리·매칭·사용 권한을 확인하세요.', 'warning');
      return;
    }
    try {
      const { loadFormTemplateBytes } = await import('system/formTemplateFileStore');
      const loaded = await loadFormTemplateBytes(form);
      await loadBuffer(loaded.bytes, loaded.fileName || form.templateFile || form.formId, {
        formId: form.formId,
      });
      showAlert(`${form.formName || form.formId} 로드`, 'info');
    } catch (err) {
      console.error(err);
      showAlert(`기관 양식 로드 실패: ${err?.message || err}`, 'error');
    }
  }, [resolveBizForm, loadBuffer]);

  const applyFieldsToEditor = useCallback(
    async (fieldValues, label) => {
      const ed = editorRef.current;
      if (!ed) {
        showAlert('에디터가 준비되지 않았습니다.', 'warning');
        return false;
      }
      setBusy(true);
      try {
        const result = await ed.putFieldMap(fieldValues, fileName || 'biz-plan.hwp');
        setLogOpen(true);
        setPanelJson(
          JSON.stringify(
            {
              gate: 'lookup-apply',
              label,
              fieldValues,
              results: result.results,
              studio: ed.studioUrl,
              fieldApi: ed.supportsFieldApi,
              appliedAt: new Date().toISOString(),
            },
            null,
            2,
          ),
        );
        const failed = (result.results || []).filter((r) => !r.ok);
        if (failed.length) {
          showAlert(`${label}: 일부 필드 반영 실패 (${failed.map((f) => f.name).join(', ')})`, 'warning');
          return false;
        }
        showAlert(`${label}을(를) 양식에 반영했습니다.`, 'success');
        return true;
      } catch (err) {
        console.error(err);
        showAlert(`필드 반영 실패: ${err?.message || err}`, 'error');
        return false;
      } finally {
        setBusy(false);
      }
    },
    [fileName],
  );

  const openDeptLookup = useCallback(() => {
    setLookup({ open: true, type: 'dept' });
  }, []);

  const openProjectLookup = useCallback(() => {
    setLookup({ open: true, type: 'project' });
  }, []);

  const onFieldClick = useCallback(
    (payload) => {
      const name = payload?.name || '';
      const form = activeForm || resolveBizForm();
      const field = (form?.fields || []).find(
        (f) => f.fieldId === name || f.hwpField === name,
      );
      const kind = lookupKindForInput(field?.input) || (name === 'header.deptName' ? 'dept' : name === 'doc.title' ? 'project' : null);
      if (kind === 'dept') openDeptLookup();
      else if (kind === 'project') openProjectLookup();
    },
    [activeForm, resolveBizForm, openDeptLookup, openProjectLookup],
  );
  const onFieldClickRef = useRef(onFieldClick);
  onFieldClickRef.current = onFieldClick;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!hostRef.current) return;
        if (editorRef.current?.destroy) {
          try {
            editorRef.current.destroy();
          } catch (_) {
            /* ignore */
          }
        }
        const host = await createBizPlanEditor(hostRef.current, {
          onFieldClick: (payload) => onFieldClickRef.current?.(payload),
        });
        if (cancelled) {
          host.destroy?.();
          return;
        }
        editorRef.current = host;
        setReady(true);
        setLoadError('');
        ensureFormCatalog();
        const form = resolveBizForm();
        setActiveForm(form);
        if (form?.publicUrl || form?.hasUploadedTemplate) {
          try {
            const { loadFormTemplateBytes } = await import('system/formTemplateFileStore');
            const loaded = await loadFormTemplateBytes(form);
            await host.loadFile(loaded.bytes, loaded.fileName || form.templateFile || form.formId, {
              suppressDialogs: true,
            });
            if (!cancelled) {
              setFileName(loaded.fileName || form.templateFile || form.formId);
            }
          } catch (e) {
            console.warn('auto load form failed', e);
          }
        }
      } catch (e) {
        console.error('[BizPlanRhwpPoc] rhwp load failed', e);
        setLoadError(
          e?.message || '@rhwp/editor 로드 실패. npm i @rhwp/editor 후 재시작하세요.',
        );
        setReady(false);
      }
    })();
    return () => {
      cancelled = true;
      if (editorRef.current?.destroy) {
        try {
          editorRef.current.destroy();
        } catch (_) {
          /* ignore */
        }
      }
      editorRef.current = null;
    };
  }, [tenantCode]); // eslint-disable-line react-hooks/exhaustive-deps -- 호스트는 기관 변경 시에만 재생성

  const lookupConfig = useMemo(() => {
    if (lookup.type === 'dept') {
      const rows = filterDepts(loadDepts(tenantCode), { useYn: 'Y' });
      return {
        title: '부서 조회 (조직관리)',
        rows: rows.map((r) => ({ ...r, id: r.deptUid || r.deptCode })),
        columns: [
          hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 100 }),
          hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 180 }),
        ],
        searchConfig: {
          mode: 'unified',
          keywordFields: ['deptCode', 'deptName'],
          codeLabel: '부서코드',
          nameLabel: '부서명',
        },
      };
    }
    if (lookup.type === 'project') {
      const loaded = loadOrSeedProjects({ tenantCode, fiscalYear }) || [];
      const rows = (Array.isArray(loaded) ? loaded : []).filter((r) => r.useYn !== 'N');
      return {
        title: `사업 조회 (사업등록 · ${fiscalYear})`,
        rows: rows.map((r) => ({ ...r, id: r.projectUid || r.projectCode })),
        columns: [
          hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 110 }),
          hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 220 }),
        ],
        searchConfig: {
          mode: 'unified',
          keywordFields: ['projectCode', 'projectName'],
          codeLabel: '사업코드',
          nameLabel: '사업명',
        },
      };
    }
    return { title: '', rows: [], columns: [], searchConfig: {} };
  }, [lookup.type, tenantCode, fiscalYear]);

  const onLookupSelect = useCallback(
    async (row) => {
      if (!row) return false;
      if (lookup.type === 'dept') {
        const deptCode = row.deptCode || '';
        const deptName = row.deptName || '';
        const nextPicked = { ...picked, deptCode, deptName };
        const form = resolveBizForm(nextPicked);
        if (form && form.formId !== activeForm?.formId) setActiveForm(form);
        const ok = await applyFieldsToEditor({ 'header.deptName': deptName }, '부서');
        if (ok) setPicked(nextPicked);
        return ok;
      }
      if (lookup.type === 'project') {
        const projectCode = row.projectCode || '';
        const projectName = row.projectName || '';
        const nextPicked = { ...picked, projectCode, projectName };
        const form = resolveBizForm(nextPicked);
        if (form && form.formId !== activeForm?.formId) setActiveForm(form);
        const ok = await applyFieldsToEditor({ 'doc.title': projectName }, '사업');
        if (ok) setPicked(nextPicked);
        return ok;
      }
      return true;
    },
    [lookup.type, applyFieldsToEditor, picked, resolveBizForm, activeForm],
  );

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFileChosen = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      try {
        await loadBuffer(new Uint8Array(await file.arrayBuffer()), file.name);
      } catch (err) {
        console.error(err);
        showAlert(`파일 열기 실패: ${err?.message || err}`, 'error');
      }
    },
    [loadBuffer],
  );

  const openPlan = useCallback(
    async (row) => {
      if (!row?.planId) return;
      setBusy(true);
      try {
        const rec = await getBizPlan(row.planId);
        if (!rec) {
          showAlert('저장된 계획서를 찾을 수 없습니다.', 'warning');
          return;
        }
        setPlanId(rec.planId);
        setPicked({
          deptCode: rec.picked?.deptCode || '',
          deptName: rec.picked?.deptName || '',
          projectCode: rec.picked?.projectCode || '',
          projectName: rec.picked?.projectName || '',
        });
        resetDraft({ ...FORMATION_DRAFT_DEFAULTS, ...fieldsToDraft(rec.fields), ...(rec.draft || {}) });
        if (rec.hwpBytes && rec.hwpBytes.length) {
          await loadBuffer(rec.hwpBytes, rec.fileName || `${rec.planId}.hwp`, {
            formId: rec.formId,
            planId: rec.planId,
          });
        } else {
          await loadTenantForm();
          const map = mergeFieldMaps(rec.fields || {}, draftToFieldMap(rec.draft, rec.picked));
          if (Object.keys(map).length) {
            await applyFieldsToEditor(map, '재오픈');
          }
        }
        showAlert(`계획서 열기 · ${rec.title || rec.planId}`, 'info');
      } catch (err) {
        showAlert(`열기 실패: ${err?.message || err}`, 'error');
      } finally {
        setBusy(false);
      }
    },
    [loadBuffer, loadTenantForm, applyFieldsToEditor, resetDraft],
  );

  useEffect(() => {
    listGrid.onRowClicked = ({ data }) => {
      if (data?.planId) openPlan(data);
    };
  }, [listGrid, openPlan]);

  const handleSearch = useCallback(async () => {
    try {
      const mock = loadBizPlanMock(tenantCode);
      const existing = await listBizPlans({ tenantCode, fiscalYear });
      if (!existing.length && mock) {
        await upsertBizPlan({
          tenantCode,
          fiscalYear,
          formId: mock.formId,
          formName: mock.formName,
          fileName: mock.fileName,
          picked: mock.picked,
          fields: mock.fields,
          draft: mock.draft,
          title: mock.picked?.projectName || mock.fields?.['doc.title'],
        });
      }
      const rows = await refreshList();
      showAlert(`조회 ${rows.length}건`, 'info');
    } catch (err) {
      showAlert(`조회 실패: ${err?.message || err}`, 'error');
    }
  }, [tenantCode, fiscalYear, refreshList]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mock = loadBizPlanMock(tenantCode);
        const existing = await listBizPlans({ tenantCode, fiscalYear });
        if (!existing.length && mock) {
          await upsertBizPlan({
            tenantCode,
            fiscalYear,
            formId: mock.formId,
            formName: mock.formName,
            fileName: mock.fileName,
            picked: mock.picked,
            fields: mock.fields,
            draft: mock.draft,
            title: mock.picked?.projectName || mock.fields?.['doc.title'],
          });
        }
        if (!cancelled) await refreshList();
      } catch (err) {
        console.warn('biz plan list init failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantCode, fiscalYear, refreshList]);

  const handleAdd = useCallback(async () => {
    setPlanId(null);
    setPicked({ deptCode: '', deptName: '', projectCode: '', projectName: '' });
    resetDraft(FORMATION_DRAFT_DEFAULTS);
    await loadTenantForm();
    showAlert('새 사업계획서 (양식 로드). 저장 시 새 ID가 부여됩니다.', 'info');
  }, [loadTenantForm, resetDraft]);

  const handleDelete = useCallback(async () => {
    const selected = listGridRef.current?.getSelectedRow?.();
    const id = selected?.planId || planId;
    if (!id) {
      showAlert('삭제할 계획서를 목록에서 선택하세요.', 'warning');
      return;
    }
    const ok = await showConfirm(`사업계획서 ${id} 를 삭제할까요?`, 'warning');
    if (!ok) return;
    await removeBizPlan(id);
    if (planId === id) {
      setPlanId(null);
      setPicked({ deptCode: '', deptName: '', projectCode: '', projectName: '' });
      resetDraft(FORMATION_DRAFT_DEFAULTS);
      await loadTenantForm();
    }
    await refreshList();
    showAlert('삭제했습니다.', 'success');
  }, [planId, loadTenantForm, refreshList, resetDraft]);

  const showFieldMap = useCallback(() => {
    setLogOpen(true);
    const form = activeForm || resolveBizForm();
    setPanelJson(
      JSON.stringify(
        {
          gate: 'field-map',
          formId: form?.formId,
          formName: form?.formName,
          publicUrl: form?.publicUrl,
          studio: editorRef.current?.studioUrl,
          fieldApi: editorRef.current?.supportsFieldApi,
          picked,
          planId,
          fields: (form?.fields || []).map((f) => ({
            fieldId: f.fieldId,
            guide: f.guide,
            syncToDb: f.syncToDb,
            syncToFormation: f.syncToFormation,
            input: f.input,
          })),
        },
        null,
        2,
      ),
    );
  }, [activeForm, resolveBizForm, picked, planId]);

  const exportHwp = useCallback(async () => {
    const ed = editorRef.current;
    if (!ed) return;
    try {
      const bytes = await ed.exportHwp();
      const blob = new Blob([bytes], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download =
        (fileName?.replace(/\.(hwp|hwpx|hml)$/i, '') || 'export') + '_export.hwp';
      a.click();
      URL.revokeObjectURL(url);
      showAlert('HWP 내보내기 완료', 'success');
    } catch (err) {
      showAlert(`exportHwp 실패: ${err?.message || err}`, 'error');
    }
  }, [fileName]);

  const savePlan = useCallback(async () => {
    const ed = editorRef.current;
    if (!ed) {
      showAlert('에디터가 준비되지 않았습니다.', 'warning');
      return;
    }
    setBusy(true);
    try {
      const form = activeForm || resolveBizForm();
      const fieldNames = (form?.fields || []).map((f) => f.hwpField || f.fieldId).filter(Boolean);
      const draftMap = draftToFieldMap(getDraftValues(), picked);

      const exported = await ed.exportHwp();
      let extracted = {};
      try {
        extracted = await ed.extractFields(fieldNames);
      } catch (e) {
        console.warn('field extract failed', e);
      }

      const fields = mergeFieldMaps(extracted, draftMap);
      if (Object.keys(fields).length) {
        await ed.putFieldMap(fields, fileName || 'biz-plan.hwp');
      }
      const bytes = await ed.exportHwp();

      const nextId = planId || createPlanId(fiscalYear);
      const meta = await upsertBizPlan({
        planId: nextId,
        tenantCode,
        fiscalYear,
        formId: form?.formId || '',
        formName: form?.formName || '',
        fileName: fileName || 'biz-plan.hwp',
        title: picked.projectName || fields['doc.title'] || '',
        picked,
        fields,
        draft: getDraftValues(),
        hwpBytes: bytes,
        status: 'DRAFT',
      });
      setPlanId(nextId);
      await ed.notifySaved(fileName || 'biz-plan.hwp');
      await refreshList();

      const filled = Object.entries(fields).filter(([, v]) => normalizeFieldValue(v));
      setLogOpen(true);
      setPanelJson(
        JSON.stringify(
          {
            gate: 'upsert',
            planId: nextId,
            formId: form?.formId,
            picked,
            extracted,
            fields,
            filledCount: filled.length,
            hwpBytesLen: bytes?.byteLength || bytes?.length,
            savedAt: meta.savedAt,
          },
          null,
          2,
        ),
      );

      if (!picked.projectCode && !normalizeFieldValue(fields['doc.title'])) {
        showAlert(
          '저장됨 · 사업명이 비어 있습니다. 누름틀 또는 「사업」버튼으로 선택한 뒤 다시 저장하세요.',
          'warning',
        );
      } else {
        showAlert(
          `저장 완료 · ${nextId}\n부서 ${picked.deptName || fields['header.deptName'] || '—'}\n사업 ${picked.projectName || fields['doc.title'] || '—'}`,
          'success',
        );
      }
    } catch (err) {
      showAlert(`저장 실패: ${err?.message || err}`, 'error');
    } finally {
      setBusy(false);
    }
  }, [
    tenantCode,
    fileName,
    picked,
    activeForm,
    getDraftValues,
    planId,
    fiscalYear,
    refreshList,
  ]);

  const actionButtons = [
    { id: 'search', label: '조회', onClick: handleSearch, hotkey: 'F7', disabled: busy },
    { id: 'add', label: '추가', onClick: handleAdd, hotkey: 'F8', disabled: !ready || busy },
    { id: 'delete', label: '삭제', onClick: handleDelete, hotkey: 'F9', disabled: busy },
    { id: 'save', label: '저장', onClick: savePlan, hotkey: 'F10', disabled: !ready || busy },
    { id: 'open', label: '파일 열기', onClick: openFilePicker, disabled: !ready || busy },
    {
      id: 'tenantForm',
      label: '기관 양식',
      onClick: loadTenantForm,
      disabled: !ready || busy,
    },
    {
      id: 'dept',
      label: '부서',
      onClick: openDeptLookup,
      disabled: !ready || busy,
      title: '조직관리 부서 목록에서 선택 → 양식 반영',
    },
    {
      id: 'project',
      label: '사업',
      onClick: openProjectLookup,
      disabled: !ready || busy,
      title: '사업등록 목록에서 선택 → 양식 반영',
    },
    { id: 'fields', label: '필드맵', onClick: showFieldMap, disabled: !ready || busy },
    { id: 'export', label: 'HWP 내보내기', onClick: exportHwp, disabled: !ready || busy },
  ];

  const extraButtons = [
    {
      id: 'log',
      label: logOpen ? '로그 접기' : '로그',
      onClick: () => setLogOpen((v) => !v),
    },
    {
      id: 'chrome',
      label: chromeCompact ? '안내 펼치기' : '안내 접기',
      onClick: () => setChromeCompact((v) => !v),
    },
  ];

  const pickedMeta = [
    planId ? `ID ${planId}` : '신규',
    picked.deptName ? `부서 ${picked.deptName}` : null,
    picked.projectName ? `사업 ${picked.projectName}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Page>
      <TopBar>
        <TitleRow>
          <CompactTitle>사업계획서 작성</CompactTitle>
          <Breadcrumb style={{ marginBottom: 0 }}>
            <span>사업계획</span>
            <span className="separator">›</span>
            <span className="current">사업계획서 작성</span>
          </Breadcrumb>
          <FileMeta title={fileName || ''}>
            {activeForm
              ? `${activeForm.formName} (${activeForm.formId})`
              : '기관 양식 없음'}
            {fileName ? ` · ${fileName}` : ''}
            {pickedMeta ? ` · ${pickedMeta}` : ''}
          </FileMeta>
        </TitleRow>
        {!chromeCompact ? (
          <Hint>
            누름틀(부서·사업)을 클릭하면 조회 팝업이 열립니다. 셀프호스트 스튜디오가 없으면
            상단 「부서」「사업」버튼을 쓰세요. 저장은 F10 (IndexedDB). SSOT:{' '}
            <code>BD/design/13_사업계획서_OSS_에디터.md</code>
          </Hint>
        ) : (
          <Hint>
            목록에서 재오픈 · F8 신규 · F10 저장. 누름틀 클릭 또는 「부서」「사업」으로 마스터 선택.
          </Hint>
        )}
        {loadError ? (
          <Hint style={{ color: 'var(--erp-danger, #b91c1c)' }}>{loadError}</Hint>
        ) : null}
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <FormProvider {...draftMethods}>
          <SyncPanel>
            <Hint style={{ marginBottom: 4 }}>
              편성연동 입력 (지출편성요구 이관용) · 부서 {picked.deptName || '—'} · 사업{' '}
              {picked.projectName || '—'}
            </Hint>
            <FormArea dense fields={syncDraftFields} />
          </SyncPanel>
        </FormProvider>
        <input
          ref={fileInputRef}
          type="file"
          accept=".hwp,.hwpx,.hml"
          style={{ display: 'none' }}
          onChange={onFileChosen}
        />
      </TopBar>

      <Shell>
        <ListHost>
          <HiGridComponent
            ref={listGridRef}
            columns={listColumns}
            height="100%"
            showRowNumber
            rowNumberSize={48}
            enableColumnFilters
            enableContextMenu={false}
            enableHeaderMenu
            gridId={LIST_GRID_ID}
            prefsUserId={prefsUserId}
            exportFileName="사업계획서목록"
            {...listGrid.contextMenuProps}
          />
        </ListHost>
        <EditorHost ref={hostRef} />
        <LogPanel $open={logOpen}>{panelJson}</LogPanel>
      </Shell>

      <CodeLookupDialog
        open={lookup.open}
        title={lookupConfig.title}
        rows={lookupConfig.rows}
        columns={lookupConfig.columns}
        enableSearch
        searchConfig={lookupConfig.searchConfig}
        prefsUserId={prefsUserId}
        sizeKey={`bizPlanLookup-${lookup.type || 'x'}`}
        onSelect={onLookupSelect}
        onClose={() => setLookup({ open: false, type: null })}
      />
    </Page>
  );
};

export default BizPlanRhwpPoc;
